import React, { useEffect, useState } from "react";
import { SupportNotifications } from 'pages/UserQueries/SupportNotifications';
import { NavLink, useLocation } from "react-router-dom";
import { FiBell, FiChevronDown, FiLogOut, FiZap } from "react-icons/fi";
import { LiaWalletSolid } from "react-icons/lia";
import { UserOutlined } from "@ant-design/icons";
import { Avatar, Dropdown } from "antd";
import { useAuth } from "context/auth-context";
import PropTypes from "prop-types";
import { ChangeLanguage } from "components/core/ChangeLanguage";
import { ThemeToggle } from "components/core/ThemeToggle/ThemeToggle";
import { BrandLogo } from "components/ui";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getEnableUserForRedeemApi, getPointBalanceStudiesWiseApi } from "../../components/Dashboard/Dasboard.api";
import { getStreakCountApi } from "../../components/Sidebar/Surveystreak/Surveystreak.api";
import { currencySymbols } from "./CurrencySymbol";
import axios from "axios";
import { getDataa, useSurveyQuery } from "./CommonComponent";
import { SurveyNotification } from "./NotificationModel/SurveyNotificationModel";
import { PointNotificationModel } from "./NotificationModel/PointNotificationModel";
import { ShowNotificationModel } from "./NotificationModel/ShowNotificationModel";
import { getNotificationCountApi } from "./NotificationModel/Notification.api";
import { useWalletDetails } from "hooks/useWalletDetails";
import { useBonusPoints } from "hooks/useBonusPointsDetails";
import { bonusDetailsType } from "types/useCustomHookTypes";
import LogoutConfirmation from "pages/Logout/LogoutConfirmationModel";
import { axiosClient } from "api/api";
import { findNavLabel, useNavSections } from "./navConfig";

const Navbar = ({ toggleSurveystreak, showSurveystreak, setShowSurveystreak }) => {
  const { auth } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const breadcrumb = findNavLabel(useNavSections(), location.pathname);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const items = [
    {
      key: "profile_1",
      className: "header-ant-dropdown-menu",
      label: (
        <span onClick={() => setShowLogoutPopup(true)} className="flex items-center gap-2">
          <FiLogOut />
          {t("home.headers.logout")}
        </span>
      ),
    },
  ];
  const [toCurrency, setToCurrency] = useState("");
  const [currentCurrency, setCurrentCurrency] = useState<number | undefined>(undefined);
  const [symbol, setSymbol] = useState(" ");
  const [, setEnabled] = React.useState<boolean>(false);
  const [country, setCountry] = useState("");
  const [langCode, setLangCode] = useState("");
  const [, setCountryChangeMessage] = useState("");
  const [searchQuery] = useState("");
  const [showNotificationModel, setShowNotificationMode] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const walletDetails: Record<string, number | undefined> = useWalletDetails();
  const bonusDetails: bonusDetailsType | null = useBonusPoints();

  const enabledUser = useQuery(["getenabledUser"], () => getEnableUserForRedeemApi(), {
    refetchOnWindowFocus: false,
  });

  // ✅ check if current user is in enabled user list
  const isUserEnableForTestSurvey = React.useMemo(() => {
    const panelListString = enabledUser?.data?.result?.[1][0]?.panelListId || "";
    const enabledIds = panelListString.split(",").map((id: string) => id.trim());

    return enabledIds.includes(String(auth.panelistId));
  }, [enabledUser, auth.panelistId]);

  const userType = isUserEnableForTestSurvey ? "test" : "all";
  const { surveys } = useSurveyQuery(country, langCode, auth.panelistId, searchQuery, userType);

  useEffect(() => {
    if (walletDetails && bonusDetails) {
      setEnabled(walletDetails?.userWalletPoints >= Number(bonusDetails?.data?.target_points));
    }
  }, [walletDetails, bonusDetails]);

  // taking currency symbol
  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };
  // taking currency code from api
  const getData = async () => {
    const ipUrl = `/api/user/get-ip-details`;
    const res = await axiosClient.get(ipUrl, { timeout: 5000 });
    setToCurrency(res?.data?.data?.country?.currency?.code);
  };
  useEffect(() => {
    getData();
    setSymbol(getCurrencySymbol(toCurrency));
  }, [toCurrency]);

  const getNotificationCount = useQuery(
    ["notificationCount", auth.panelistId, showNotificationModel],
    () => getNotificationCountApi(auth.panelistId),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Use `refetch` whenever the modal state changes
  useEffect(() => {
    getNotificationCount.refetch();
    setNotificationCount(getNotificationCount?.data?.unreadCount);
  }, [notificationCount, getNotificationCount, showNotificationModel]);

  const getPointsBalance = useQuery(["pointBal", auth.panelistId], () => getPointBalanceStudiesWiseApi(auth.panelistId), {
    refetchOnWindowFocus: false,
  });

  const getSurveyvirtualwallet = useQuery(["streakdata"], () => getStreakCountApi(auth?.panelistId, 0), {
    refetchOnWindowFocus: false,
  });

  const pointBalance = getPointsBalance && getPointsBalance?.data ? getPointsBalance?.data?.data : 0;

  const topSurvey = surveys
    ? surveys
        .map((survey) => ({
          studyName: survey.studyName,
          CPI: survey.vendorCpi * 100,
          studyId: survey._id,
        }))
        .sort((a, b) => b.CPI - a.CPI)
    : [];

  const TotalWalletBalance = walletDetails?.userWalletPoints || 0;
  const totalDollar = TotalWalletBalance / 100;

  useEffect(() => {
    const calculateTotalWalletDollar = async () => {
      try {
        if (toCurrency !== undefined) {
          // Fetch exchange rate from Fixer API
          const response = await axios.get("https://cdn.jsdelivr.net/gh/ismartcoding/currency-api@main/latest/data.json");

          const quotes = response.data.quotes;

          if (quotes[toCurrency]) {
            const rate = quotes[toCurrency];
            setCurrentCurrency(totalDollar * rate);
          }
        }
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    };

    calculateTotalWalletDollar();
  }, [toCurrency, totalDollar]);

  useEffect(() => {
    getDataa({
      setCountry,
      setLangCode,
      setCountryChangeMessage,
      auth,
      t,
    });
  }, []);

  const firstName = auth.name ? auth.name.split(" ")[0] : "";

  return (
    <div>
      <div className="zs-topbar">
        <NavLink to="/Dashboard" className="hidden sm:flex lg:hidden shrink-0" aria-label="Dashboard">
          <BrandLogo className="w-auto h-7" />
        </NavLink>

        {breadcrumb && (
          <nav aria-label="Breadcrumb" className="items-center hidden min-w-0 gap-2 text-sm lg:flex">
            <span className="text-ink-3">{breadcrumb.section}</span>
            <span className="text-ink-4">/</span>
            <span className="font-semibold truncate text-ink">{breadcrumb.page}</span>
          </nav>
        )}

        <div className="flex items-center justify-end flex-1 min-w-0 gap-1.5 sm:gap-2">
          {/* Wallet balance — hover shows the local-currency equivalent */}
          <div className="zs-topbar-chip group" tabIndex={0}>
            <span className="zs-topbar-chip-icon bg-primary-soft text-primary-text">
              <LiaWalletSolid className="w-4 h-4" />
            </span>
            <span className="tabular-nums">{+TotalWalletBalance}</span>
            <span className="hidden font-medium sm:inline text-ink-3">{t("referFriend.Points")}</span>
            <span className="zs-tooltip">{`${symbol} ${currentCurrency?.toFixed(2)}`}</span>
          </div>

          {/* Survey streak */}
          <button
            type="button"
            className={`zs-topbar-chip ${showSurveystreak ? "!border-success/40 !bg-success-soft" : ""}`}
            onClick={toggleSurveystreak}
            aria-expanded={!!showSurveystreak}
            aria-label="Survey streak"
          >
            <span className="zs-topbar-chip-icon bg-success-soft text-success">
              <FiZap className="w-3.5 h-3.5" />
            </span>
            <span className="tabular-nums text-success">{getSurveyvirtualwallet?.data?.completeCount}</span>
          </button>

          {/* Notifications */}
          <SupportNotifications onOpenTicket={() => setShowNotificationMode(false)}>{(supportCount, supportContent) => (
          <div className="relative">
            <button
              type="button"
              className="zs-icon-btn"
              aria-label="Notifications"
              aria-expanded={showNotificationModel}
              onClick={() => {
                setShowNotificationMode(true);
                setShowSurveystreak(false);
              }}
            >
              <FiBell className="w-[18px] h-[18px]" />
              {/* The rewards count is undefined while loading or when the account has no panelist ID (e.g. admins);
                  treat that as 0 so support messages still reach the badge instead of the sum becoming NaN. */}
              {(Number(notificationCount) || 0) + supportCount > 0 && <span className="zs-count-badge">{(Number(notificationCount) || 0) + supportCount}</span>}
            </button>

            {showNotificationModel && (
              <ShowNotificationModel
                supportCount={supportCount}
                supportContent={supportContent}
                showNotificationModel={showNotificationModel}
                setShowNotificationModel={setShowNotificationMode}
                setNotificationCount={setNotificationCount}
                notificationCount={notificationCount}
              />
            )}
          </div>
          )}</SupportNotifications>

          <ThemeToggle compact className="sm:hidden" />
          <ThemeToggle className="hidden sm:inline-flex" />

          <ChangeLanguage />

          <span className="hidden w-px h-6 mx-1 bg-line sm:block" aria-hidden="true" />

          <Dropdown menu={{ items }} placement="bottomRight">
            <a className="flex items-center gap-2 pl-0.5 pr-1 py-1 rounded-full cursor-pointer hover:bg-surface-hover transition-colors">
              {auth.profile_pic ? (
                <img
                  src={auth.profile_pic}
                  alt="Profile"
                  className="object-cover w-8 h-8 rounded-full ring-2 ring-surface shadow-xs"
                />
              ) : (
                <Avatar size={32} icon={<UserOutlined />} className="!bg-primary-soft !text-primary-text" />
              )}
              <span className="hidden text-[13px] font-semibold leading-tight sm:flex text-ink">Hi! {firstName}</span>
              <FiChevronDown className="hidden w-4 h-4 sm:block text-ink-3" />
            </a>
          </Dropdown>
        </div>
      </div>

      {showLogoutPopup && <LogoutConfirmation onClose={() => setShowLogoutPopup(false)} />}

      {topSurvey.length > 0 && (
        <SurveyNotification
          survey={topSurvey[0]}
          setNotificationCount={setNotificationCount}
          notificationCount={notificationCount}
        />
      )}

      <PointNotificationModel
        pointBalance={pointBalance}
        setNotificationCount={setNotificationCount}
        notificationCount={notificationCount}
      />
    </div>
  );
};
Navbar.propTypes = {
  toggleSurveystreak: PropTypes.func.isRequired,
  showSurveystreak: PropTypes.bool,
  setShowSurveystreak: PropTypes.func,
};
export default Navbar;
